/**
 * Examine Flavor Text Definitions
 * 
 * Centralized repository for all examine/inspect flavor text in the game.
 * Provides atmospheric descriptions for every interactable object.
 */

export interface ExamineText {
  title: string;
  description: string;
  islandId?: string;
}

/**
 * Island Examine Text
 */
export const getIslandExamineText = (islandData: {
  islandId?: string;
  permanentName?: string;
  isPermanent?: boolean;
  biome?: string;
  size?: string;
}): ExamineText => {
  if (islandData.isPermanent && islandData.permanentName) {
    const permanentDescriptions: Record<string, string> = {
      'Arcadia': 'The Regime\'s capital city. Massive stone structures dominate the skyline.',
      'Stormveil Depths': 'Once the heart of the Ironheart Confederation\'s forges. Now shrouded in perpetual storms. Ruins of advanced metallurgy workshops lie buried beneath the waves.',
      'Stillwater Veil': 'Waters that remember the past. This region once hosted the Skyborn Confederacy\'s floating cities. Strange weather patterns persist—remnants of ancient technology.',
      'Eastern Reach': 'Verdant forests stretch across this territory. The Verdant Concord once built cities within ancient trees. Some say their descendants still hide in remote enclaves.',
      'Western Reach': 'Desert monuments stand as silent witnesses to the Sunstone Empire\'s glory. The Sunstone Citadel rises from the sands, partially buried by time.',
      'Northern Reach': 'Mountains of ice and stone. The Ironheart Confederation\'s northern strongholds still stand, though now claimed by the Regime. Advanced crafting secrets lie dormant here.',
      'Southern Reach': 'A maritime region built on the ruins of canal-cities. The Thalassian Dominion\'s shipbuilding legacy lives on in modern vessel designs.',
      'Paradise': 'A region of dangerous storms and mysterious phenomena. The Skyborn Confederacy once ruled these skies. Legends speak of sky islands that appear during tempests.',
      'Sunfire Archipelago': 'Desert islands with massive stone monuments. The Sunstone Empire\'s astronomical knowledge is encoded in the very architecture of these ruins.',
      'Frostwind Isles': 'Northern islands of ice and industry. The Ironheart Confederation\'s forges once burned bright here. Raiders now claim the abandoned strongholds.',
      'Crimson Meridian': 'A scar across reality itself. The Riftfall tore through this region, leaving physics twisted and strange. Ancient ruins predate the catastrophe.',
    };

    return {
      title: islandData.permanentName,
      description: permanentDescriptions[islandData.permanentName] || 'A location steeped in the history of the Grand Latitude. Its past shapes its present.',
      islandId: islandData.islandId,
    };
  }

  const biomeDescriptions: Record<string, string> = {
    'tropical': 'Lush vegetation thrives in the warm climate. The sand is white and inviting. This island offers respite, though danger lurks beneath the surface.',
    'volcanic': 'Blackened rock and active vents dominate the landscape. The air shimmers with heat. This island is alive with raw elemental power.',
    'frozen': 'Ice and snow blanket everything. The wind cuts like a blade. Only the hardiest survive in this unforgiving environment.',
    'desert': 'Sand stretches endlessly in all directions. The sun is relentless. Resources are scarce, and every decision matters.',
    'jungle': 'Towering trees create a canopy that blocks the sky. The sounds of unseen life echo constantly. Nature rules here, not civilization.',
  };

  const biome = islandData.biome || 'tropical';
  return {
    title: 'Uncharted Island',
    description: biomeDescriptions[biome] || 'An island waiting to be discovered. The Grand Latitude holds countless secrets for those brave enough to seek them.',
    islandId: islandData.islandId,
  };
};

/**
 * Fishing Spot Examine Text
 */
export const getFishingSpotExamineText = (spotType: string): ExamineText => {
  const descriptions: Record<string, { title: string; description: string }> = {
    'coral_reef': {
      title: 'Coral Reef',
      description: 'Vibrant coral formations teem with life. Schools of colorful fish dart between the branches. The water is crystal clear—perfect for fishing.',
    },
    'kelp_forest': {
      title: 'Kelp Forest',
      description: 'Tall kelp stalks sway in the current like an underwater forest. Larger fish hide in the shadows. This is where predators hunt.',
    },
    'shallow_waters': {
      title: 'Shallow Waters',
      description: 'The water is calm and shallow here. Smaller fish gather in abundance. An ideal spot for beginners to practice their technique.',
    },
    'deep_trench': {
      title: 'Deep Trench',
      description: 'The ocean floor drops away into darkness. Strange shapes move in the depths. Only the bravest fishermen dare cast their lines here.',
    },
    'shipwreck_site': {
      title: 'Shipwreck Site',
      description: 'The remains of a sunken vessel rest on the seafloor. Fish have made it their home. Treasures may still lie within the wreckage.',
    },
  };

  return descriptions[spotType] || {
    title: 'Fishing Spot',
    description: 'The water ripples with activity. Fish are abundant here. A good place to cast your line.',
  };
};

/**
 * Ship Examine Text
 */
export const getShipExamineText = (shipData: {
  isNPC?: boolean;
  level?: number;
  ownerName?: string;
  shipType?: string;
  faction?: string;
}): ExamineText => {
  if (shipData.isNPC) {
    const factionDescriptions: Record<string, string> = {
      'Navy': 'A Navy patrol vessel. The crew is disciplined and well-armed. They enforce the Regime\'s maritime law across the Grand Latitude.',
      'Crimson Corsairs': 'A Crimson Corsairs ship. The crew bears the mark of one of the Grand Latitude\'s most feared pirate factions. They strike with precision.',
      'Stormbreaker Syndicate': 'A Stormbreaker Syndicate vessel. The crew moves with calculated efficiency. They are known for their ruthless tactics.',
      'Obsidian Reavers': 'An Obsidian Reavers ship. The crew looks hardened and dangerous. They are among the most brutal pirates sailing these waters.',
      'Merchant Guild': 'A Merchant Guild trading vessel. The ship is laden with cargo. The crew is cautious but professional.',
      'Smugglers Network': 'A smuggler\'s ship. The crew keeps to themselves. They move through the shadows of the Grand Latitude.',
    };

    const defaultDescriptions = [
      'A merchant vessel laden with cargo. The crew appears cautious—they know these waters are dangerous.',
      'An independent ship. The crew bears no obvious faction colors. Their intentions are unclear.',
      'A fishing boat. The crew hauls in nets filled with the day\'s catch. They seem indifferent to your presence.',
      'A salvage vessel. The crew scavenges wreckage and debris. They are always searching for the next prize.',
      'A scout ship. The crew scans the horizon constantly. They gather intelligence for someone.',
    ];

    const description = factionDescriptions[shipData.faction || ''] || 
                       defaultDescriptions[Math.floor(Math.random() * defaultDescriptions.length)];

    return {
      title: shipData.ownerName || 'Unknown Vessel',
      description,
    };
  }

  return {
    title: shipData.ownerName || 'Player Ship',
    description: `A ${shipData.shipType || 'sturdy'} vessel. The crew appears experienced. They have weathered the storms of the Grand Latitude.`,
  };
};

/**
 * Whirlpool Examine Text
 */
export const getWhirlpoolExamineText = (whirlpoolClass: string): ExamineText => {
  const descriptions: Record<string, string> = {
    'A': 'A massive whirlpool churns with violent force. The water spirals down into an abyss. Only the most experienced sailors dare enter.',
    'B': 'A dangerous whirlpool pulls at your ship. The current is strong but navigable. Treasures may lie within.',
    'C': 'A moderate whirlpool swirls before you. The water is turbulent but manageable. A test of skill for any sailor.',
    'D': 'A small whirlpool spins lazily. The current is weak. Even novice sailors can navigate this safely.',
  };

  return {
    title: `Class ${whirlpoolClass} Whirlpool`,
    description: descriptions[whirlpoolClass] || 'A swirling vortex of water. The depths below are unknown.',
  };
};

/**
 * Loot Container Examine Text
 */
export const getLootContainerExamineText = (containerType?: string): ExamineText => {
  return {
    title: 'Floating Wreckage',
    description: 'Debris from a destroyed ship floats on the waves. Supplies and treasures may still be salvageable. The sea claims everything eventually.',
  };
};

/**
 * Resource Node Examine Text
 */
export const getResourceNodeExamineText = (resourceType: string): ExamineText => {
  const descriptions: Record<string, { title: string; description: string }> = {
    'wood': {
      title: 'Driftwood',
      description: 'Weathered timber floats in the water. Suitable for repairs and construction. The sea provides for those who know where to look.',
    },
    'stone': {
      title: 'Rocky Outcrop',
      description: 'Jagged rocks protrude from the water. Valuable minerals can be extracted with the right tools. The stone is ancient and weathered.',
    },
    'metal': {
      title: 'Metal Debris',
      description: 'Twisted metal from a shipwreck. Can be salvaged and reforged. The rust tells a story of battles long past.',
    },
    'herbs': {
      title: 'Medicinal Plants',
      description: 'Rare herbs grow on this floating vegetation. Useful for treating wounds and illness. Nature provides remedies for those who seek them.',
    },
  };

  return descriptions[resourceType] || {
    title: 'Resource Node',
    description: 'A source of valuable materials. Harvesting requires time and effort.',
  };
};

/**
 * Ambient NPC Examine Text
 */
export const getAmbientNPCExamineText = (npcType: string, npcName?: string): ExamineText => {
  const descriptions: Record<string, string> = {
    'sailor': 'A weathered sailor drifts on a small boat. Their eyes are weary but sharp. They may have goods to trade or stories of the Grand Latitude to tell.',
    'fisherman': 'A lone fisherman casts their line into the water. Their boat is laden with the day\'s catch. They might be willing to barter for supplies.',
    'merchant': 'A traveling merchant navigates the seas. Their vessel is packed with goods from across the Grand Latitude. They always have something interesting for sale.',
    'pirate': 'A pirate crew patrols these waters. They bear the colors of one of the Grand Latitude\'s notorious factions. Approach with caution.',
    'marine': 'A Navy officer commands a patrol boat. They enforce the Regime\'s maritime law. Independent sailors are viewed with suspicion.',
    'scholar': 'A scholar studies ancient texts and artifacts. They seek knowledge of the Old Kingdom and the Architects. Information is their currency.',
    'smuggler': 'A smuggler moves through the shadows. They traffic in contraband and secrets. Their network spans the Grand Latitude.',
  };

  return {
    title: npcName || 'Wandering Sailor',
    description: descriptions[npcType] || 'A stranger on the open sea. Their role in the Grand Latitude\'s complex web of factions is unclear.',
  };
};

/**
 * Debris (Message in Bottle) Examine Text
 */
export const getDebrisExamineText = (): ExamineText => {
  return {
    title: 'Message in a Bottle',
    description: 'A sealed bottle floats on the waves. A rolled parchment is visible inside. Someone sent this message hoping it would be found.',
  };
};

/**
 * Shipwreck Examine Text
 */
export const getShipwreckExamineText = (quality: string): ExamineText => {
  const descriptions: Record<string, string> = {
    'common': 'The remains of a small vessel. The wreckage is picked over but may still hold minor supplies. Scavengers have been here before.',
    'uncommon': 'A moderately sized shipwreck. The hull is cracked but intact. Valuable salvage may remain inside.',
    'rare': 'A large merchant vessel lies broken on the seafloor. The cargo hold is still sealed. Significant treasures await those brave enough to dive.',
    'epic': 'The ruins of a naval warship. The cannons are still mounted. This ship went down fighting—its treasures are worth the risk.',
    'legendary': 'A legendary vessel from a bygone era. The craftsmanship is extraordinary. Whatever sank this ship must have been terrifying.',
  };

  return {
    title: `Shipwreck [${quality.toUpperCase()}]`,
    description: descriptions[quality] || 'A sunken ship rests on the ocean floor. Time and tide have claimed another victim.',
  };
};

/**
 * Rift Scar Examine Text
 */
export const getRiftScarExamineText = (scarData: {
  name?: string;
  grade?: string;
  type?: string;
}): ExamineText => {
  const gradeDescriptions: Record<string, string> = {
    'A': 'Reality fractures at this location. The air shimmers with unstable energy. Only the most prepared should enter—death is almost certain.',
    'B': 'A dangerous tear in space. Strange phenomena occur near the rift. Experienced adventurers may survive what lies within.',
    'C': 'A moderate rift scar. The distortion is visible but contained. Skilled sailors can navigate the dangers inside.',
    'D': 'A minor rift anomaly. The energy is weak but present. Even novices can explore this safely.',
  };

  return {
    title: scarData.name || 'Rift Scar',
    description: gradeDescriptions[scarData.grade || 'D'] || 'A tear in reality. The Architects left these scars across the Grand Line. What lies beyond is unknown.',
  };
};

/**
 * Sanctum Container Examine Text
 */
export const getSanctumContainerExamineText = (rarity?: string): ExamineText => {
  const descriptions: Record<string, string> = {
    'common': 'A sealed container from the Architect era. The lock is simple. Basic supplies likely remain inside.',
    'uncommon': 'An Architect storage unit. The seal is intact. Valuable equipment may be preserved within.',
    'rare': 'A reinforced Architect vault. The locking mechanism is complex. Rare artifacts await those who can open it.',
    'epic': 'A high-security Architect container. The seal glows with residual energy. Epic treasures are stored inside.',
    'legendary': 'A legendary Architect cache. The container hums with power. Whatever is inside was deemed too valuable to lose.',
  };

  return {
    title: `Architect Container [${(rarity || 'common').toUpperCase()}]`,
    description: descriptions[rarity || 'common'] || 'A sealed container from the Architect civilization. Ancient technology preserves its contents.',
  };
};

/**
 * World/Navigation Examine Text
 */
export const getWorldExamineText = (): ExamineText => {
  return {
    title: 'Open Sea',
    description: 'Endless water stretches in all directions. The horizon blurs where sky meets ocean. The Grand Line is vast and unforgiving.',
  };
};
